import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, Search, FileDown, FileText, ArrowUpDown, MoreVertical, MapPin, Flag, Clock, User } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import AddressBlock, { emptyAddr, normAddr } from "@/components/AddressBlock";
import { deriveShiftsFromTrips, fmtDurationMins } from "@/lib/worktimeFromTrips";
import DriverDetailModal from "@/components/DriverDetailModal";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import TripExportDialog from "@/components/TripExportDialog";
import { useAuth } from "@/contexts/AuthContext";

const STATUS = ["geplant", "unterwegs", "abgeschlossen", "storniert"];
const STATUS_LABEL = { geplant: "Geplant", unterwegs: "Unterwegs", abgeschlossen: "Abgeschlossen", storniert: "Storniert" };
const emptyTrip = () => ({
  pickups: [{ ...emptyAddr }],
  destinations: [{ ...emptyAddr }],
  trip_date: new Date().toISOString().slice(0, 10),
  start_time: "08:00",
  end_time: "",
  driver_id: "",
  vehicle_id: "",
  status: "geplant",
  notes: "",
});

export default function Trips() {
  const { user } = useAuth();
  const canEdit = user.role !== "fahrer";
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  // Sync the global header search (?q=) into the page search.
  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setSearch(q);
  }, [searchParams]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "trip_date", dir: "desc" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTrip());
  const [detailDriverId, setDetailDriverId] = useState(null);
  const detailDriver = useMemo(() => drivers.find((d) => d.id === detailDriverId) || null, [drivers, detailDriverId]);
  const [detailVehicleId, setDetailVehicleId] = useState(null);
  const detailVehicle = useMemo(() => vehicles.find((v) => v.id === detailVehicleId) || null, [vehicles, detailVehicleId]);
  const [exportKind, setExportKind] = useState(null); // "csv" | "pdf" | null

  const load = async () => {
    try {
      const [t, d, v] = await Promise.all([api.get("/trips"), api.get("/drivers"), api.get("/vehicles")]);
      setTrips(t.data); setDrivers(d.data); setVehicles(v.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let arr = trips.filter((t) => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!q) return true;
      const addrs = [...(t.pickups || [t.pickup_address]), ...(t.destinations || [t.destination_address])].filter(Boolean);
      const addrText = addrs.map((a) => `${a.city || ""} ${a.street || ""} ${a.note || ""} ${a.special || ""}`).join(" ");
      return [t.driver_name, t.line_label, t.license_plate, addrText, t.notes, t.trip_date, t.start_time, t.end_time]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
    arr.sort((a, b) => {
      const k = sort.key;
      const av = (a[k] || "") + ""; const bv = (b[k] || "") + "";
      return sort.dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return arr;
  }, [trips, search, statusFilter, sort]);

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  const normAddrLocal = normAddr;
  const startEdit = (t) => {
    setEditing(t);
    const pickups = (t.pickups && t.pickups.length ? t.pickups : [t.pickup_address]).filter(Boolean).map(normAddrLocal);
    const destinations = (t.destinations && t.destinations.length ? t.destinations : [t.destination_address]).filter(Boolean).map(normAddrLocal);
    setForm({
      ...emptyTrip(),
      ...t,
      pickups: pickups.length ? pickups : [{ ...emptyAddr }],
      destinations: destinations.length ? destinations : [{ ...emptyAddr }],
      driver_id: t.driver_id || "",
      vehicle_id: t.vehicle_id || "",
      notes: t.notes || "",
    });
    setOpen(true);
  };

  // address list helpers
  const updateAddr = (kind, i, addr) => setForm((f) => ({ ...f, [kind]: f[kind].map((a, idx) => idx === i ? addr : a) }));
  const addAddr = (kind) => setForm((f) => ({ ...f, [kind]: [...f[kind], { ...emptyAddr }] }));
  const rmAddr = (kind, i) => setForm((f) => ({ ...f, [kind]: f[kind].length <= 1 ? f[kind] : f[kind].filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      if (!form.driver_id || !form.vehicle_id) {
        const missing = !form.driver_id && !form.vehicle_id ? "Fahrer und Fahrzeug" : (!form.driver_id ? "Fahrer" : "Fahrzeug");
        toast.warning(`Hinweis: ${missing} nicht zugewiesen – die Fahrt wird trotzdem gespeichert.`);
      }
      const payload = {
        ...form,
        pickups: form.pickups,
        destinations: form.destinations,
        driver_id: form.driver_id || null,
        vehicle_id: form.vehicle_id || null,
      };
      if (editing) { await api.patch(`/trips/${editing.id}`, payload); toast.success("Fahrt aktualisiert"); }
      else { await api.post("/trips", payload); toast.success("Fahrt angelegt"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeTrip = async (t) => {
    if (!window.confirm("Fahrt wirklich löschen?")) return;
    try { await api.delete(`/trips/${t.id}`); toast.success("Fahrt gelöscht"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (t, status) => {
    try { await api.patch(`/trips/${t.id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadExport = (kind) => setExportKind(kind);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl heading font-semibold">Fahrten</h1>
          <p className="text-sm text-muted-foreground">Fahrtenübersicht und Erfassung.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="trips-export-button"><FileDown className="h-4 w-4 mr-1" /> Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => downloadExport("csv")} data-testid="export-csv"><FileDown className="h-4 w-4 mr-2" /> CSV herunterladen</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => downloadExport("pdf")} data-testid="export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF herunterladen</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => window.print()} data-testid="export-print">Drucken</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <Card className="p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suche: Fahrgast, Fahrer, Linie, Ort…" className="pl-9" data-testid="trips-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="trips-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <Th onClick={() => toggleSort("trip_date")}>Datum</Th>
              <Th onClick={() => toggleSort("start_time")}>Start</Th>
              <th className="text-left px-4 py-2">Ende</th>
              <th className="text-left px-4 py-2">Abholung</th>
              <th className="text-left px-4 py-2">Ziel</th>
              <th className="text-left px-4 py-2">Fahrer</th>
              <th className="text-left px-4 py-2">Linie</th>
              <th className="text-left px-4 py-2">Kennzeichen</th>
              <th className="text-left px-4 py-2">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const pks = (t.pickups && t.pickups.length ? t.pickups : [t.pickup_address]).filter(Boolean);
              const dts = (t.destinations && t.destinations.length ? t.destinations : [t.destination_address]).filter(Boolean);
              return (
                <tr key={t.id} className="border-t border-border row-hover" data-testid={`trip-row-${t.id}`}>
                  <td className="px-4 py-2 tabular-nums">{t.trip_date}</td>
                  <td className="px-4 py-2 tabular-nums">{t.start_time}</td>
                  <td className="px-4 py-2 tabular-nums">{t.end_time || "—"}</td>
                  <td className="px-4 py-2 text-muted-foreground">{addrSummary(pks[0])}{pks.length > 1 && <span className="ml-1 text-xs text-blue-600">+{pks.length - 1}</span>}</td>
                  <td className="px-4 py-2 text-muted-foreground">{addrSummary(dts[0])}{dts.length > 1 && <span className="ml-1 text-xs text-blue-600">+{dts.length - 1}</span>}</td>
                  <td className="px-4 py-2">{t.driver_id ? (
                    <button onClick={() => setDetailDriverId(t.driver_id)} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`trip-driver-link-${t.id}`}>
                      {t.driver_name || "—"}
                    </button>
                  ) : "—"}</td>
                  <td className="px-4 py-2">{t.line_label || "—"}</td>
                  <td className="px-4 py-2 font-mono">{t.vehicle_id ? (
                    <button onClick={() => setDetailVehicleId(t.vehicle_id)} className="text-blue-700 dark:text-blue-300 hover:underline" data-testid={`trip-plate-link-${t.id}`}>
                      {t.license_plate || "—"}
                    </button>
                  ) : "—"}</td>
                  <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-2 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" data-testid={`trip-actions-${t.id}`}><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canEdit && <DropdownMenuItem onSelect={() => startEdit(t)} data-testid={`trip-edit-${t.id}`}><Pencil className="h-4 w-4 mr-2" /> Bearbeiten</DropdownMenuItem>}
                        {STATUS.map((s) => (
                          <DropdownMenuItem key={s} onSelect={() => setStatus(t, s)} data-testid={`trip-status-${t.id}-${s}`}>Status: {STATUS_LABEL[s]}</DropdownMenuItem>
                        ))}
                        {canEdit && <DropdownMenuItem onSelect={() => removeTrip(t)} className="text-red-600" data-testid={`trip-delete-${t.id}`}><Trash2 className="h-4 w-4 mr-2" /> Löschen</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="px-4 py-6 text-center text-muted-foreground text-sm">Keine Fahrten gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Fahrt bearbeiten" : "Neue Fahrt"}</DialogTitle><DialogDescription>Beliebig viele Abhol- und Zieladressen mit Bemerkung und Besonderheiten erfassen.</DialogDescription></DialogHeader>
          <div className="space-y-5">
            {/* Pickups */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold heading text-muted-foreground uppercase tracking-wider flex items-center gap-1"><MapPin className="h-4 w-4" /> Abholadressen</h3>
                <Button type="button" size="sm" variant="outline" onClick={() => addAddr("pickups")} data-testid="trip-add-pickup"><Plus className="h-3.5 w-3.5 mr-1" /> Weitere Abholadresse</Button>
              </div>
              <div className="space-y-4">
                {form.pickups.map((a, i) => (
                  <AddressBlock key={i} index={i} value={a} prefix="pickup" removable={form.pickups.length > 1}
                    onRemove={() => rmAddr("pickups", i)} onChange={(v) => updateAddr("pickups", i, v)} />
                ))}
              </div>
            </section>

            {/* Destinations */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold heading text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Flag className="h-4 w-4" /> Zieladressen</h3>
                <Button type="button" size="sm" variant="outline" onClick={() => addAddr("destinations")} data-testid="trip-add-destination"><Plus className="h-3.5 w-3.5 mr-1" /> Weitere Zieladresse</Button>
              </div>
              <div className="space-y-4">
                {form.destinations.map((a, i) => (
                  <AddressBlock key={i} index={i} value={a} prefix="dest" removable={form.destinations.length > 1}
                    onRemove={() => rmAddr("destinations", i)} onChange={(v) => updateAddr("destinations", i, v)} />
                ))}
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold heading mb-2 text-muted-foreground uppercase tracking-wider">Fahrtinformationen</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Datum</Label><Input type="date" value={form.trip_date} onChange={(e) => setForm({ ...form, trip_date: e.target.value })} data-testid="trip-date-input" /></div>
                <div><Label>Startzeit Fahrt</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="trip-start-input" /></div>
                <div><Label>Endzeit Fahrt</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="trip-end-input" /></div>
                <div>
                  <Label>Fahrer</Label>
                  <Select value={form.driver_id || "none"} onValueChange={(v) => setForm({ ...form, driver_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="trip-driver-select"><SelectValue placeholder="Fahrer wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— keiner —</SelectItem>
                      {drivers.filter((d) => d.active).map((d) => <SelectItem key={d.id} value={d.id}>{d.firstname} {d.lastname}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fahrzeug</Label>
                  <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="trip-vehicle-select"><SelectValue placeholder="Fahrzeug wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— keines —</SelectItem>
                      {vehicles.filter((v) => v.active).map((v) => <SelectItem key={v.id} value={v.id}>{v.license_plate}{v.vehicle_type ? ` · ${v.vehicle_type}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="trip-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Bemerkungen (Fahrt)</Label>
                  <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="trip-notes-input" />
                </div>
              </div>
            </section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} data-testid="trip-save-button">{editing ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriverId(null)} />
      <VehicleDetailModal vehicle={detailVehicle} open={!!detailVehicle} onOpenChange={(o) => !o && setDetailVehicleId(null)} />
      <TripExportDialog open={!!exportKind} onOpenChange={(o) => !o && setExportKind(null)} kind={exportKind || "csv"} drivers={drivers} vehicles={vehicles} />
    </div>
  );
}

function WorktimeFromTripsCard({ trips, onOpenDriver }) {
  const shifts = useMemo(() => deriveShiftsFromTrips(trips), [trips]);
  const totalMinutes = shifts.reduce((s, x) => s + (x.duration_minutes || 0), 0);
  const totalTrips = shifts.reduce((s, x) => s + (x.trip_count || 0), 0);
  const uniqueDrivers = new Set(shifts.map((s) => s.driver_id)).size;

  return (
    <Card className="overflow-hidden" data-testid="trips-worktime-summary">
      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold heading">Arbeitszeit aus Fahrten</h2>
        </div>
        <div className="ml-auto flex items-center gap-4 text-xs text-muted-foreground tabular-nums">
          <span>Fahrer: <span className="font-semibold text-foreground" data-testid="wt-summary-drivers">{uniqueDrivers}</span></span>
          <span>Fahrten: <span className="font-semibold text-foreground" data-testid="wt-summary-trips">{totalTrips}</span></span>
          <span>Gesamt: <span className="font-semibold text-foreground" data-testid="wt-summary-total">{fmtDurationMins(totalMinutes)}</span></span>
        </div>
      </div>
      {shifts.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground italic">Noch keine zugewiesenen Fahrten – Arbeitszeiten werden hier aus der Wochenplanung berechnet.</div>
      ) : (
        <div className="max-h-[260px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
              <tr>
                <th className="text-left px-4 py-2">Datum</th>
                <th className="text-left px-4 py-2">Fahrer</th>
                <th className="text-left px-4 py-2 tabular-nums">Start</th>
                <th className="text-left px-4 py-2 tabular-nums">Ende</th>
                <th className="text-left px-4 py-2 tabular-nums">Dauer</th>
                <th className="text-left px-4 py-2 tabular-nums">Fahrten</th>
                <th className="text-left px-4 py-2">Fahrzeuge</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((sh) => (
                <tr key={`${sh.driver_id}-${sh.date}`} className="border-t border-border row-hover" data-testid={`wt-shift-row-${sh.driver_id}-${sh.date}`}>
                  <td className="px-4 py-2 tabular-nums whitespace-nowrap">{sh.date}</td>
                  <td className="px-4 py-2">
                    <button onClick={() => onOpenDriver(sh.driver_id)} className="text-blue-700 dark:text-blue-300 hover:underline inline-flex items-center gap-1" data-testid={`wt-shift-driver-${sh.driver_id}-${sh.date}`}>
                      <User className="h-3 w-3" /> {sh.driver_name || "—"}
                    </button>
                  </td>
                  <td className="px-4 py-2 tabular-nums">{sh.start_time}</td>
                  <td className="px-4 py-2 tabular-nums">{sh.end_time || "—"}</td>
                  <td className="px-4 py-2 tabular-nums font-semibold">{fmtDurationMins(sh.duration_minutes)}</td>
                  <td className="px-4 py-2 tabular-nums">{sh.trip_count}</td>
                  <td className="px-4 py-2 text-muted-foreground truncate max-w-[240px]">{sh.vehicles.join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function Th({ children, onClick }) {
  return (
    <th className="text-left px-4 py-2 cursor-pointer select-none" onClick={onClick}>
      <span className="inline-flex items-center gap-1">{children} <ArrowUpDown className="h-3 w-3 opacity-60" /></span>
    </th>
  );
}

function addrSummary(a) {
  if (!a) return "—";
  const street = [a.street, a.house_number].filter(Boolean).join(" ").trim();
  const cityLine = [a.zip, a.city].filter(Boolean).join(" ").trim();
  return [street, cityLine].filter(Boolean).join(", ") || "—";
}
